import {SharedGraph} from '../../navigation/SharedGraph';
import {readSharedReport, useSharedReport} from '../../navigation/useSharedReport';
// src/shared/ui/table/CommonTable.jsx

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useTableSearch } from './TableSearchScope';
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import { exportCommonTableToExcel } from "../../utils/excel/exportCommonTableToExcel.js";

/**
 * CommonTable
 *
 * NEW (opt-in): Row expansion (accordion under each row)
 *  - Controlled mode (preferred):
 *      expandedRowIds: { [rowId]: boolean }
 *      setExpandedRowIds: (nextObj) => void
 *  - Uncontrolled fallback:
 *      defaultExpandedRowIds: string[]
 *
 * NEW (opt-in): Seamless expanded row styling
 *  - expandedRowCellClassName: className applied to the <td> wrapping expanded content
 *  - expandedRowCellStyle: style applied to the <td> wrapping expanded content
 *  - expandedRowRowClassName: className applied to the <tr> wrapping expanded content
 *
 * NEW (opt-in): hideColumnHeaders
 *  - hides the <thead> column header row (useful for nested / expanded tables)
 */
const EMPTY_EXPANDED_ROWS = [];

export const CommonTableOptionsContext = React.createContext({});
export const CommonTable = props => {
  const defaults = React.useContext(CommonTableOptionsContext);
  return <SharedGraph kind="table" title={props.title || `table-${(props.columns || []).map(column => column.key).join('-')}`} shareId={props.shareId}>
    {id => <SharedCommonTable key={id} {...defaults} {...props} shareId={id}/>}
  </SharedGraph>;
};
const SharedCommonTable = ({
  columns,
  data,
  shareId,
  isEditing = false,
  onChange = () => {},
  headerColor = "bg-gray-200",
  title,
  toolbarHeading = null,
  showAggregateTotal = true,
  onAggregateTotal = () => {},
  defaultSortField = null,
  defaultSortOrder = "asc",
  // Optional external sorting for datasets paginated by the caller.
  sortField: controlledSortField,
  sortOrder: controlledSortOrder,
  onSortChange = null,
  manualSorting = false,
  resetSortKey = undefined,
  sectionOrder = null,

  // Row expansion (opt-in)
  enableRowExpansion = false,

  // support BOTH names to avoid silent bugs
  isRowExpandable = null,
  rowIsExpandable = null,

  renderExpandedRow = null,
  getRowId = null,

  // Uncontrolled default
  defaultExpandedRowIds = EMPTY_EXPANDED_ROWS,

  // Controlled expansion (object map)
  expandedRowIds = null,
  setExpandedRowIds = null,

  // UX options
  expandOnRowClick = true,
  expandOnLabelClick = false,
  showExpandCaret = false,

  filterEmptyRows = true,

  // NEW: allow hiding header row (used by nested tables)
  hideColumnHeaders = false,
  searchable = true,
  showCopyLink = true,

  // Excel export (opt-in)
  excelExportRef = null, // React ref object from parent
  excelFilename = "table-export.xlsx",
  excelSheetName = "Table",
  excelTitle = null,
  excelIncludeAggregate = true,

  // NEW: styling hooks for expanded row container so it can look seamless
  expandedRowCellClassName = null,
  expandedRowCellStyle = null,
  expandedRowRowClassName = null,
}) => {
  const [shared] = useState(() => readSharedReport(shareId));
  const [localSearch, setSearch] = useState(typeof shared.search === 'string' ? shared.search : '');
  const searchScope = useTableSearch();
  const search = searchScope?.query ?? localSearch;
  const registerTable = searchScope?.register;
  useEffect(() => registerTable?.(shareId), [registerTable, shareId]);
  const [copyStatus, setCopyStatus] = useState('');
  const restoredSearch = React.useRef(false);
  useEffect(() => {
    if (!restoredSearch.current && searchScope && typeof shared.search === 'string') {
      restoredSearch.current = true;
      searchScope.setQuery(shared.search);
    }
  }, [searchScope, shared]);
  const [localSortField, setSortField] = useState(columns.some(column => column.key === shared.sort) ? shared.sort : defaultSortField);
  const [localSortOrder, setSortOrder] = useState(["asc", "desc"].includes(shared.order) ? shared.order : defaultSortOrder);

  const sortField = controlledSortField === undefined ? localSortField : controlledSortField;
  const sortOrder = controlledSortOrder === undefined ? localSortOrder : controlledSortOrder;

  // Reports can restore their ranking when the selected reporting period changes.
  const previousSortKey = React.useRef(resetSortKey);
  useEffect(() => {
    if (previousSortKey.current === resetSortKey) return;
    previousSortKey.current = resetSortKey;
    setSortField(defaultSortField);
    setSortOrder(defaultSortOrder);
  }, [resetSortKey, defaultSortField, defaultSortOrder]);

  // kept for your currency-editing UX
  const [activeEditCell, setActiveEditCell] = useState(null);
  const [draftValue, setDraftValue] = useState("");

  const hasExpansion =
    enableRowExpansion && typeof renderExpandedRow === "function";

  // Choose expandability function (prefer isRowExpandable, then rowIsExpandable, else default)
  const isRowExpandableFn = useMemo(() => {
    if (typeof isRowExpandable === "function") return isRowExpandable;
    if (typeof rowIsExpandable === "function") return rowIsExpandable;
    return () => true;
  }, [isRowExpandable, rowIsExpandable]);

  /**
   * Controlled vs uncontrolled expansion state
   * - Controlled: expandedRowIds is object map, setExpandedRowIds provided.
   * - Uncontrolled: internal Set.
   */
  const isControlled =
    hasExpansion &&
    expandedRowIds &&
    typeof expandedRowIds === "object" &&
    typeof setExpandedRowIds === "function";

  const [internalExpanded, setInternalExpanded] = useState(() => {
    return new Set(
      Array.isArray(shared.expanded) ? shared.expanded : Array.isArray(defaultExpandedRowIds) ? defaultExpandedRowIds : [],
    );
  });

  useEffect(() => {
    if (!hasExpansion) return;
    if (Array.isArray(shared.expanded)) return;
    if (isControlled) return;
    if (!Array.isArray(defaultExpandedRowIds)) return;
    setInternalExpanded(new Set(defaultExpandedRowIds));
  }, [hasExpansion, isControlled, defaultExpandedRowIds, shared.expanded]);

  const activateShare = useSharedReport(shareId, undefined, {search, sort: sortField, order: sortOrder, expanded: isControlled ? Object.keys(expandedRowIds).filter(key => expandedRowIds[key]) : [...internalExpanded]});
  const linkRef = React.useRef(null);
  linkRef.current = activateShare.link;
  const registerLink = searchScope?.registerLink;
  useEffect(() => {
    if (isEditing) return undefined;
    return registerLink?.(shareId, () => linkRef.current());
  }, [registerLink, shareId, isEditing]);
  // Section search lives outside the table; activate sharing when it changes.
  const previousSearch = React.useRef(search);
  useEffect(() => {
    if (previousSearch.current !== search) { previousSearch.current = search; if (!searchScope || searchScope.isShareTarget(shareId)) activateShare(); }
  }, [search, activateShare, searchScope, shareId]);

  const restoredExpansion = React.useRef(false);
  useEffect(() => {
    if (isControlled && Array.isArray(shared.expanded) && !restoredExpansion.current) {
      restoredExpansion.current = true;
      setExpandedRowIds(Object.fromEntries(shared.expanded.map(key => [key, true])));
    }
    const wrapper = document.getElementById(shareId);
    if (wrapper) wrapper.dataset.shareTableState = JSON.stringify({search, sort:sortField, order:sortOrder, expanded:isControlled ? Object.keys(expandedRowIds).filter(key => expandedRowIds[key]) : [...internalExpanded]});
  }, [shareId, search, sortField, sortOrder, internalExpanded, expandedRowIds, isControlled, setExpandedRowIds, shared]);

  const isExpanded = useCallback(
    (rowId) => {
      if (!hasExpansion) return false;
      if (isControlled) return !!expandedRowIds?.[rowId];
      return internalExpanded.has(rowId);
    },
    [hasExpansion, isControlled, expandedRowIds, internalExpanded],
  );

  const toggleExpanded = useCallback(
    (rowId) => {
      if (!hasExpansion) return;

      if (isControlled) {
        setExpandedRowIds((prev) => {
          const next = { ...(prev || {}) };
          next[rowId] = !next[rowId];
          return next;
        });
        return;
      }

      setInternalExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(rowId)) next.delete(rowId);
        else next.add(rowId);
        return next;
      });
    },
    [hasExpansion, isControlled, setExpandedRowIds],
  );

  /**
   * Returns the effective sort key for a given column key.
   */
  const getSortKey = (colKey) => {
    const col = columns.find((c) => c.key === colKey);
    return col?.sortKey || colKey;
  };

  function humanizeSectionLabel(raw) {
    if (typeof raw !== "string") return raw;
    const s = raw.trim();
    if (!s) return s;

    const spaced = s
      .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
      .replace(/[_-]+/g, " ")
      .trim();

    const withAmp = spaced.replace(/\bAnd\b/g, "&");

    return withAmp
      .split(/\s+/)
      .map((w) => {
        if (w === "&") return "&";
        if (/^[A-Z0-9]+$/.test(w)) return w;
        return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
      })
      .join(" ");
  }

  const normalizedData = useMemo(() => {
    return Array.isArray(data)
      ? data
      : data?.adopted?.items || data?.items || [];
  }, [data]);

function rowHasAnyValue(row, cols) {
  return (cols || []).some((col) => {
    // columns we ignore for "has content" checks
    if (
      col.key === "label" ||
      col.key === "increase" ||
      col.key === "percentIncrease"
    ) {
      return false;
    }

    const val = row?.[col.key];

    // null/undefined => no
    if (val == null) return false;

    // Numbers: meaningful only if finite and non-zero
    if (typeof val === "number") {
      return Number.isFinite(val) && val !== 0;
    }

    // Strings: meaningful if non-empty AND not just "0" (or "$0", "0.00", etc.)
    if (typeof val === "string") {
      const s = val.trim();
      if (s === "") return false;

      // If it's a numeric-like string, treat zero as "no content"
      const normalized = s.replace(/[$,()\s]/g, ""); // strips common currency formatting
      const n = Number(normalized);
      if (Number.isFinite(n)) return n !== 0;

      return true; // non-numeric strings count
    }

    // Booleans: true counts as meaningful
    if (typeof val === "boolean") return val === true;

    // Everything else: conservative default (don't count objects/arrays as "numeric content")
    return false;
  });
}

  const handleSort = (field) => {
    if (onSortChange) {
      onSortChange(field, sortField === field && sortOrder === "asc" ? "desc" : "asc");
      return;
    }
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const getCellStyle = () => ({
    whiteSpace: "normal",
    overflow: "visible",
    textOverflow: "clip",
    wordBreak: "break-word",
    verticalAlign: "top",
  });

  const getAlignClass = (col) => {
    if (col.align === "right") return "text-right";
    if (col.align === "center") return "text-center";
    return "text-left";
  };

  const getHeaderAlignProps = (col) => {
    const align = col.headerAlign || "center";
    if (align === "right")
      return { cellClass: "text-right", justifyClass: "justify-end" };
    if (align === "left")
      return { cellClass: "text-left", justifyClass: "justify-start" };
    return { cellClass: "text-center", justifyClass: "justify-center" };
  };

  function naturalize(str = "") {
    return String(str)
      .split(/(\d+)/)
      .map((s) => (isNaN(s) ? s.toLowerCase() : parseInt(s, 10)));
  }

  function naturalCompare(a, b) {
    const aParts = naturalize(a);
    const bParts = naturalize(b);
    const len = Math.max(aParts.length, bParts.length);
    for (let i = 0; i < len; i++) {
      if (aParts[i] === undefined) return -1;
      if (bParts[i] === undefined) return 1;
      if (aParts[i] < bParts[i]) return -1;
      if (aParts[i] > bParts[i]) return 1;
    }
    return 0;
  }

  const formatPercentText = (val) => {
    if (val == null || Number.isNaN(val)) return "—";
    const num = Number(val);
    const isNegative = num < 0;
    const base = Math.abs(num) <= 1 ? num * 100 : num; // supports 0.025 -> 2.5%
    const pctText = `${Math.abs(base).toFixed(1)}%`;
    return isNegative ? `(${pctText})` : pctText;
  };

  const formatCurrencyText = (val) => {
    if (val == null || Number.isNaN(val)) return "—";
    const num = Number(val);
    const isNegative = num < 0;
    const abs = Math.abs(num);
    const text = `$${abs.toLocaleString()}`;
    return isNegative ? `(${text})` : text;
  };

  const renderCurrency = (val) => {
    if (val == null || Number.isNaN(val)) return "—";
    const num = Number(val);
    const text = formatCurrencyText(num);
    const isNegative = num < 0;
    return <span className={isNegative ? "text-red-600" : ""}>{text}</span>;
  };

  const renderPercent = (val) => {
    if (val == null || Number.isNaN(val)) return "—";
    const num = Number(val);
    const text = formatPercentText(num);
    const isNegative = num < 0;
    return <span className={isNegative ? "text-red-600" : ""}>{text}</span>;
  };

  const sanitizeEditableCurrencyDraft = (raw) => {
    const s = String(raw ?? "").trim();
    if (s === "" || s === "-") return s;

    const isNeg = s.startsWith("-");
    const cleaned = s.replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    const normalized =
      parts.length <= 1 ? parts[0] : `${parts[0]}.${parts.slice(1).join("")}`;

    return isNeg ? `-${normalized}` : normalized;
  };

  const getCellId = (row, colKey) => `${row?.label ?? ""}__${colKey}`;

  const toEditableCurrencyString = (num) => {
    if (typeof num !== "number" || !Number.isFinite(num)) return "";
    return String(num);
  };

  const isTotalLikeLabel = (lbl) => {
    const s = String(lbl || "")
      .trim()
      .toUpperCase();
    if (!s) return false;
    return s.startsWith("TOTAL ");
  };

  const isFundBalanceJune30Total = (lbl) => {
    const s = String(lbl || "")
      .trim()
      .toUpperCase();
    return s === "TOTAL FUND BALANCE - JUNE 30";
  };

  const isUfbJune30 = (lbl) => {
    const s = String(lbl || "")
      .trim()
      .toUpperCase();
    return s === "UNASSIGNED FUND BALANCE (UFB) - JUNE 30";
  };

  function rowCompare(a, b) {
    // keep proposed fallback at bottom regardless of sort
    if (a?.isProposedFallback && !b?.isProposedFallback) return 1;
    if (b?.isProposedFallback && !a?.isProposedFallback) return -1;

    // Pin Current Levy to top
    if (a?.label === "Current Levy") return -1;
    if (b?.label === "Current Levy") return 1;

    // default sort (no active sortField): prefer explicit order then label
    if (!sortField) {
      if (typeof a?.order === "number" && typeof b?.order === "number") {
        return a.order - b.order;
      }
      return String(a?.label || "").localeCompare(String(b?.label || ""));
    }

    const key = getSortKey(sortField);
    const aVal = a?.[key];
    const bVal = b?.[key];

    // null/undefined bottom
    if (aVal == null) return 1;
    if (bVal == null) return -1;

    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
    }

    if (aVal instanceof Date && bVal instanceof Date) {
      return sortOrder === "asc"
        ? aVal.getTime() - bVal.getTime()
        : bVal.getTime() - aVal.getTime();
    }

    if (typeof aVal === "string" && typeof bVal === "string") {
      const cmp = naturalCompare(aVal, bVal);
      return sortOrder === "asc" ? cmp : -cmp;
    }

    const aStr = aVal?.toString?.() ?? "";
    const bStr = bVal?.toString?.() ?? "";
    return sortOrder === "asc"
      ? aStr.localeCompare(bStr)
      : bStr.localeCompare(aStr);
  }

  function sortSectionRows(rows) {
    const normalRows = rows.filter(
      (r) => r?.rowType !== "subtotal" && r?.label !== "",
    );
    const subtotalRows = rows.filter(
      (r) => r?.rowType === "subtotal" || r?.label === "",
    );
    const sorted = [...normalRows].sort(rowCompare);
    return [...sorted, ...subtotalRows];
  }

  const sortedData = useMemo(() => {
    if (manualSorting) return normalizedData;
    // filter out empty numeric rows, but keep special rows
const cleanedData = (normalizedData || []).filter((row) => {
  if (!row) return false;

  // Always keep special rows
  if (row.section) return true;
  if (row.isSpacerRow) return true;
  if (row.footerType === "projectionFootnote") return true;
  if (row.isFooterRow) return true;
  if (
    typeof row.label === "string" &&
    row.label.toUpperCase().startsWith("TOTAL ")
  )
    return true;

  // ✅ Opt-in escape hatch: don't drop "empty" rows
  if (filterEmptyRows === false) return true;

  // Default behavior (existing logic)
  return rowHasAnyValue(row, columns);
});

    let base = [];

    const hasSections = cleanedData.some((row) => row.section);

    if (hasSections) {
      // block by section header
      const blocks = [];
      let currentHeader = null;
      let sectionRows = [];

      for (const row of cleanedData) {
        if (row.section) {
          if (currentHeader) {
            blocks.push({
              header: currentHeader,
              rows: sortSectionRows(sectionRows),
            });
            sectionRows = [];
          }
          currentHeader = row;
        } else {
          sectionRows.push(row);
        }
      }

      if (currentHeader) {
        blocks.push({
          header: currentHeader,
          rows: sortSectionRows(sectionRows),
        });
      }

      // optional explicit section order
      if (Array.isArray(sectionOrder) && sectionOrder.length > 0) {
        blocks.sort((a, b) => {
          const ia = sectionOrder.indexOf(a.header.section);
          const ib = sectionOrder.indexOf(b.header.section);
          const aIdx = ia === -1 ? Number.MAX_SAFE_INTEGER : ia;
          const bIdx = ib === -1 ? Number.MAX_SAFE_INTEGER : ib;
          return aIdx - bIdx;
        });
      }

      base = blocks.flatMap((block) => [block.header, ...block.rows]);
      return base;
    }

    // Non-section table: normal sort...
    base = [...cleanedData].sort(rowCompare);

    // ...then post-process totals rules expected by tests:
    // 1) UFB June 30 always last
    // 2) TOTAL rows move to bottom EXCEPT "TOTAL FUND BALANCE - JUNE 30" which stays inline
    const ufb = [];
    const totals = [];
    const keep = [];

    for (const r of base) {
      const lbl = r?.label;
      if (isUfbJune30(lbl)) {
        ufb.push(r);
        continue;
      }
      if (isTotalLikeLabel(lbl) && !isFundBalanceJune30Total(lbl)) {
        totals.push(r);
        continue;
      }
      keep.push(r);
    }

    return [...keep, ...totals, ...ufb];
  }, [normalizedData, sortField, sortOrder, sectionOrder, columns, filterEmptyRows, manualSorting]);

  const aggregateTotal = useMemo(() => {
    if (!showAggregateTotal) return null;

    // exclude blank-label subtotal rows from aggregate; keep it aligned with prior behavior
    const rows = (sortedData || []).filter(
      (row) => row && row.label !== "TOTAL" && row.label !== "" && !row.section,
    );

    const total = { label: "AGGREGATE TOTAL" };

    (columns || []).forEach((col) => {
      if (col.key !== "label") {
        total[col.key] = rows.reduce((sum, row) => {
          const val = row?.[col.key];
          return typeof val === "number" && Number.isFinite(val)
            ? sum + val
            : sum;
        }, 0);
      }
    });

    const prev = total.y1_adopted;
    const curr = total.y0_adopted;
    total.increase =
      typeof prev === "number" && typeof curr === "number" ? curr - prev : null;
    total.percentIncrease =
      typeof prev === "number" && prev !== 0 && typeof curr === "number"
        ? ((curr - prev) / prev) * 100
        : null;

    return total;
  }, [showAggregateTotal, sortedData, columns]);

  const exportToExcel = useCallback(() => {
    const rows = Array.isArray(sortedData) ? sortedData : [];

    const agg =
      excelIncludeAggregate && showAggregateTotal && aggregateTotal
        ? aggregateTotal
        : null;

    exportCommonTableToExcel({
      columns,
      rows,
      aggregateRow: agg,
      filename: excelFilename,
      sheetName: excelSheetName,
      title: excelTitle || title || null,
    });
  }, [
    sortedData,
    columns,
    aggregateTotal,
    showAggregateTotal,
    title,
    excelIncludeAggregate,
    excelFilename,
    excelSheetName,
    excelTitle,
  ]);

  // Expose imperative API for parent controls (disk icon near Ending Year, etc.)
  useEffect(() => {
    if (!excelExportRef || typeof excelExportRef !== "object") return;

    excelExportRef.current = {
      exportToExcel,
    };

    return () => {
      // avoid stale refs when unmounting
      if (excelExportRef.current?.exportToExcel === exportToExcel) {
        excelExportRef.current = null;
      }
    };
  }, [excelExportRef, exportToExcel]);

  useEffect(() => {
    if (showAggregateTotal && aggregateTotal && onAggregateTotal) {
      onAggregateTotal(aggregateTotal);
    }
  }, [showAggregateTotal, aggregateTotal, onAggregateTotal]);

  // Row ID
  const getRowIdEffective = useCallback(
    (row, rowIdx) => {
      if (typeof getRowId === "function") {
        const v = getRowId(row, rowIdx);
        return v != null ? String(v) : String(rowIdx);
      }
      const candidate =
        row?.id ?? row?._id ?? row?.docId ?? row?.__docId ?? row?.label;
      return candidate != null ? String(candidate) : String(rowIdx);
    },
    [getRowId],
  );

  const isInteractiveTarget = (target) => {
    const el = target instanceof Element ? target : null;
    if (!el) return false;
    return !!el.closest("input, textarea, select, button, a, [role='button']");
  };

  // Caret UI
  const Caret = ({ open }) => (
    <span
      className={`inline-block transition-transform duration-150 ${
        open ? "rotate-90" : ""
      }`}
      aria-hidden="true"
    >
      ▶
    </span>
  );

  // Defaults that preserve existing visuals unless you override
  const expandedTdClass = expandedRowCellClassName ?? "px-2 py-2 bg-gray-50";
  const expandedTdStyle = expandedRowCellStyle ?? getCellStyle();
  const expandedTrClass = expandedRowRowClassName ?? "border-b border-gray-400";

  const numericColumns = useMemo(() => {
    return (columns || []).filter((c) => c?.key && c.key !== "label");
  }, [columns]);

  const renderProjectionFootnoteRow = (row, rowKey) => {
    const numericCount = numericColumns.length;
    const pct = row?.growthRatePct;
    const pctText =
      typeof pct === "number" && Number.isFinite(pct) ? pct.toFixed(1) : "—";

    return (
      <tr key={rowKey} className="border-b border-gray-400">
        {/* label cell */}
        <td className="px-2 py-1 text-left" style={getCellStyle()}>
          {" "}
        </td>

        {/* merged cell spanning all numeric columns */}
        <td
          className="px-2 py-1 text-left"
          style={getCellStyle()}
          colSpan={Math.max(1, numericCount)}
        >
          <span className="font-semibold">ADOPTED BUDGET</span>{" "}
          <span className="text-gray-700">Estimated @ {pctText}%</span>
        </td>
      </tr>
    );
  };

  const matchesSearch = row => !searchable || isEditing || !search.trim() || columns.some(col => String(row[col.key] ?? '').toLowerCase().includes(search.trim().toLowerCase()));
  const matchingCount = sortedData.filter(matchesSearch).length;
  return (
    <div onChange={activateShare} onClick={() => { searchScope?.selectTable(shareId); activateShare(); }} className="w-full overflow-x-auto">
      {(toolbarHeading || (showCopyLink && !isEditing && !searchScope?.hasToolbar)) && <div className="flex flex-wrap items-center justify-between gap-2 py-1">
        {toolbarHeading && <h3 className="text-lg font-semibold">{toolbarHeading}</h3>}
        {showCopyLink && !isEditing && !searchScope?.hasToolbar && <div className="ml-auto flex items-center">
        <button type="button" aria-label={`Copy link to ${title || 'this table'}`} className="rounded px-2 py-1 text-xs text-blue-700 hover:bg-blue-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-600" onClick={async () => {
          const url = activateShare.link();
          try {
            if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
            await navigator.clipboard.writeText(url); setCopyStatus('Link copied');
          } catch { setCopyStatus('Link ready in address bar'); }
        }}>Copy link</button><span role="status" className="self-center text-xs text-slate-600">{copyStatus}</span>
        </div>}
      </div>}
      {searchable && !searchScope && !hideColumnHeaders && !isEditing && <div className="flex flex-wrap items-center gap-3 py-3">
        <label className="text-sm font-medium text-slate-700">Search {title || 'table'}<input type="search" value={search} onChange={event => setSearch(event.target.value)} className="ml-2 rounded border border-slate-300 bg-white px-3 py-2 text-slate-900" /></label>
        {search && <button type="button" onClick={() => setSearch('')} className="rounded bg-slate-100 px-3 py-2 text-slate-800">Clear search</button>}
        <span className="text-sm text-slate-600">{matchingCount.toLocaleString()} of {sortedData.length.toLocaleString()} rows{search && ' · Totals and exports include all rows.'}</span>
      </div>}
      <table className="border-collapse bg-white text-gray-800 text-sm table-fixed">
        <colgroup>
          {(columns || []).map((col) => {
            const style = col.style || {};
            const width = style.width || "100px";

            return (
              <col
                key={col.key}
                style={{
                  width,
                  minWidth: style.minWidth,
                  maxWidth: style.maxWidth,
                }}
              />
            );
          })}
        </colgroup>

        <thead>
          {title && (
            <tr>
              <th
                colSpan={columns.length}
                className="text-left px-2 py-2 font-bold text-base uppercase text-gray-700 bg-white border-none"
              >
                {title}
              </th>
            </tr>
          )}

          {!hideColumnHeaders && (
            <tr>
              {(columns || []).map((col) => {
                const { cellClass, justifyClass } = getHeaderAlignProps(col);
                return (
                  <th
                    key={col.key}
                    scope="col"
                    aria-sort={sortField === col.key ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
                    className={`px-2 py-1 text-black ${headerColor} border-none cursor-pointer select-none align-bottom ${cellClass}`}
                    style={getCellStyle()}
                    onClick={() => handleSort(col.key)}
                  >
                    <button type="button" className={`flex w-full items-center ${justifyClass} gap-1 bg-transparent text-inherit rounded px-1 py-2`} aria-label={`Sort by ${col.label}`}>
                      <span>{col.label}</span>
                      {sortField === col.key &&
                        (sortOrder === "asc" ? (
                          <ChevronUpIcon className="w-4 h-4" />
                        ) : (
                          <ChevronDownIcon className="w-4 h-4" />
                        ))}
                    </button>
                  </th>
                );
              })}
            </tr>
          )}
        </thead>

        <tbody>
          {(!sortedData?.length || matchingCount === 0) && <tr><td colSpan={Math.max(1, columns.length)} className="px-4 py-8 text-center text-slate-600">No matching records to display.</td></tr>}
          {(sortedData || []).map((row, rowIdx) => {
            if (searchable && !isEditing && search.trim() && !row.section && !row.isFooterRow && !columns.some(col => String(row[col.key] ?? '').toLowerCase().includes(search.trim().toLowerCase()))) return null;
            // SECTION HEADER ROW
            if (row?.section) {
              return (
                <tr key={`section-${rowIdx}`} className="bg-gray-100">
                  <td
                    colSpan={columns.length}
                    className="font-bold px-2 py-0.5 text-sm uppercase"
                  >
                    {humanizeSectionLabel(row.section)}
                  </td>
                </tr>
              );
            }

            // PROJECTION FOOTNOTE (special merged-cell row)
            if (row?.footerType === "projectionFootnote") {
              return renderProjectionFootnoteRow(row, `foot-${rowIdx}`);
            }

            const rowId = getRowIdEffective(row, rowIdx);
            const canExpand = hasExpansion
              ? !!isRowExpandableFn(row, rowIdx)
              : false;
            const open = canExpand ? isExpanded(rowId) : false;

            const trUserClass = row?.rowClassName || row?.rowClass || "";

            const onRowClick = (e) => {
              if (!hasExpansion) return;
              if (!expandOnRowClick) return;
              if (!canExpand) return;
              if (isInteractiveTarget(e.target)) return;
              if (expandOnLabelClick) return; // label-only mode
              toggleExpanded(rowId);
            };

            return (
              <React.Fragment key={`frag-${rowId}-${rowIdx}`}>
                <tr
                  onClick={hasExpansion ? onRowClick : undefined}
                  className={`border-b border-gray-400 ${trUserClass} ${
                    hasExpansion &&
                    canExpand &&
                    expandOnRowClick &&
                    !expandOnLabelClick
                      ? "cursor-pointer"
                      : ""
                  }`}
                >
                  {(columns || []).map((col, colIdx) => {
                    const isLabelCol = col.key === "label" || colIdx === 0;

                    const onLabelClick = (e) => {
                      if (!hasExpansion) return;
                      if (!expandOnLabelClick) return;
                      if (!canExpand) return;
                      if (isInteractiveTarget(e.target)) return;
                      e.stopPropagation();
                      toggleExpanded(rowId);
                    };

                    const cellValue = row?.[col.key];

                    const isTotalRow =
                      typeof row?.label === "string" &&
                      row.label.toUpperCase().startsWith("TOTAL ");
                    const isEditableBlocked =
                      isTotalRow || row?.isProposedFallback;

                    return (
                      <td
                        key={col.key}
                        className={`px-2 py-1 ${getAlignClass(col)} ${
                          hasExpansion &&
                          canExpand &&
                          expandOnLabelClick &&
                          isLabelCol
                            ? "cursor-pointer"
                            : ""
                        }`}
                        style={{ ...getCellStyle(), ...(col.style || {}) }}
                        onClick={isLabelCol ? onLabelClick : undefined}
                      >
                        {isLabelCol &&
                        hasExpansion &&
                        canExpand &&
                        showExpandCaret ? (
                          <div className="flex items-start gap-2">
                            <Caret open={open} />
                            <div className="flex-1">
                              {col.render
                                ? col.render(row, rowIdx)
                                : (row[col.key] ?? "—")}
                            </div>
                          </div>
                        ) : isEditing &&
                          col.editable === true &&
                          !isEditableBlocked &&
                          typeof cellValue === "number" &&
                          Number.isFinite(cellValue) ? (
                          (() => {
                            const isCurrency = col.format === "currency";
                            const cellId = getCellId(row, col.key);
                            const isActive = activeEditCell === cellId;

                            if (!isCurrency) {
                              return (
                                <input
                                  type="number"
                                  className="w-full border border-gray-300 rounded px-1 py-0.5 text-sm"
                                  value={cellValue}
                                  onClick={(ev) => ev.stopPropagation()}
                                  onFocus={(ev) => ev.stopPropagation()}
                                  onChange={(e) =>
                                    onChange(e, row.label, col.key)
                                  }
                                />
                              );
                            }

                            const displayValue = isActive
                              ? draftValue
                              : formatCurrencyText(cellValue);

                            return (
                              <input
                                type="text"
                                inputMode="decimal"
                                className="w-full border border-gray-300 rounded px-1 py-0.5 text-sm text-right"
                                value={displayValue}
                                onClick={(ev) => ev.stopPropagation()}
                                onFocus={(ev) => {
                                  ev.stopPropagation();
                                  setActiveEditCell(cellId);
                                  setDraftValue(
                                    toEditableCurrencyString(cellValue),
                                  );
                                }}
                                onChange={(e) => {
                                  const next = sanitizeEditableCurrencyDraft(
                                    e.target.value,
                                  );
                                  setDraftValue(next);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    e.currentTarget.blur();
                                  }
                                  if (e.key === "Escape") {
                                    e.preventDefault();
                                    setActiveEditCell(null);
                                    setDraftValue("");
                                    e.currentTarget.blur();
                                  }
                                }}
                                onBlur={() => {
                                  const next =
                                    sanitizeEditableCurrencyDraft(draftValue);

                                  if (next === "" || next === "-") {
                                    setActiveEditCell(null);
                                    setDraftValue("");
                                    return;
                                  }

                                  onChange(
                                    { target: { value: next } },
                                    row.label,
                                    col.key,
                                  );

                                  setActiveEditCell(null);
                                  setDraftValue("");
                                }}
                              />
                            );
                          })()
                        ) : col.render ? (
                          col.render(row, rowIdx)
                        ) : col.format === "currency" &&
                          typeof cellValue === "number" ? (
                          renderCurrency(cellValue)
                        ) : col.format === "percent" &&
                          typeof cellValue === "number" ? (
                          renderPercent(cellValue)
                        ) : typeof cellValue === "number" ? (
                          cellValue.toLocaleString("en-US")
                        ) : (
                          (cellValue ?? "—")
                        )}
                      </td>
                    );
                  })}
                </tr>

                {hasExpansion && canExpand && open ? (
                  <tr className={expandedTrClass}>
                    <td
                      colSpan={columns.length}
                      className={expandedTdClass}
                      style={{ ...expandedTdStyle }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {renderExpandedRow(row, rowIdx)}
                    </td>
                  </tr>
                ) : null}
              </React.Fragment>
            );
          })}

          {showAggregateTotal && aggregateTotal && (
            <tr className="border-t border-black font-semibold bg-gray-50">
              {(columns || []).map((col) => {
                const val = aggregateTotal?.[col.key];
                return (
                  <td
                    key={col.key}
                    className={`px-2 py-1 ${getAlignClass(col)}`}
                    style={{ ...getCellStyle(), ...(col.style || {}) }}
                  >
                    {col.render
                      ? col.render(aggregateTotal, "AGG")
                      : col.format === "currency" && typeof val === "number"
                        ? renderCurrency(val)
                        : col.format === "percent" && typeof val === "number"
                          ? renderPercent(val)
                          : (val ?? "—")}
                  </td>
                );
              })}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
